"use client";

import React, { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import Button from "@/components/ui/Button";
import FloatingSelect from "@/components/ui/FloatingSelect";
import axiosInstance, { endpoints } from "@/lib/axios";
import { REPRESENTE_OPTIONS } from "./organismeData";

// ─── Types ──────────────────────────────────────────────────────────────────

interface OrganismeFormData {
  represente: string;
  entreprise: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
}

type FieldErrors = Partial<Record<keyof OrganismeFormData, string>>;

const INITIAL_DATA: OrganismeFormData = {
  represente: "",
  entreprise: "",
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
};

// ─── Shared styles (aligned with the existing form components) ────────────────

const INPUT_BASE =
  "w-full h-10 px-3 bg-white border rounded-lg text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 transition-colors";
const INPUT_CLASS = `${INPUT_BASE} border-[#EFEFEF] focus:border-[#40BFDE]`;
const INPUT_ERROR_CLASS = `${INPUT_BASE} border-red-400 focus:border-red-500`;
const ERROR_CLASS = "mt-1 text-[10px] text-red-500";

const REQUIRED_MESSAGE = "Ce champ est obligatoire";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Validation ───────────────────────────────────────────────────────────────

// Ordered to match the visual field order so focus lands on the first
// visually-invalid field on submit.
function validate(data: OrganismeFormData): FieldErrors {
  const errors: FieldErrors = {};
  if (!data.represente) errors.represente = REQUIRED_MESSAGE;
  if (!data.entreprise.trim()) errors.entreprise = REQUIRED_MESSAGE;
  if (!data.nom.trim()) errors.nom = REQUIRED_MESSAGE;
  if (!data.prenom.trim()) errors.prenom = REQUIRED_MESSAGE;
  if (!data.email.trim()) errors.email = REQUIRED_MESSAGE;
  else if (!EMAIL_REGEX.test(data.email)) errors.email = "Adresse email invalide";
  if (!data.telephone.trim()) errors.telephone = REQUIRED_MESSAGE;
  return errors;
}

// ─── Reusable text field ────────────────────────────────────────────────────

interface TextFieldProps {
  id: keyof OrganismeFormData;
  label: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: string;
}

function TextField({
  id,
  label,
  placeholder,
  value,
  onChange,
  error,
  type = "text",
  autoComplete,
}: TextFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-base text-[#1B365E]">
        {label}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-required="true"
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={error ? INPUT_ERROR_CLASS : INPUT_CLASS}
      />
      {error && (
        <p id={`${id}-error`} role="alert" aria-live="polite" className={ERROR_CLASS}>
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Success state ────────────────────────────────────────────────────────────

function SuccessState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center py-8 text-center"
      role="status"
      aria-live="polite"
    >
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#40BFDE]/15">
        <Check className="h-7 w-7 text-[#40BFDE]" strokeWidth={2.5} aria-hidden="true" />
      </span>
      <h3 className="mb-2 font-red-hat text-[22px] font-semibold text-[#1B365E]">
        Demande envoyée !
      </h3>
      <p className="max-w-[300px] text-[13px] leading-5.5 text-[#1D1D1D]/80">
        Merci, votre demande d&apos;inscription a bien été reçue. Notre équipe vous recontacte sous
        24&nbsp;h pour référencer votre organisme.
      </p>
    </motion.div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

const RegistrationForm: React.FC = () => {
  const [data, setData] = useState<OrganismeFormData>(INITIAL_DATA);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const clearError = useCallback((field: keyof OrganismeFormData) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setData((prev) => ({ ...prev, [name]: value }));
      clearError(name as keyof OrganismeFormData);
    },
    [clearError]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      document.getElementById(Object.keys(validationErrors)[0])?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const representeLabel =
        REPRESENTE_OPTIONS.find((o) => o.value === data.represente)?.label ?? data.represente;

      // Reuse the shared public leads endpoint (same flow as the contact form).
      await axiosInstance.post(endpoints.leads.create, {
        first_name: data.prenom.trim(),
        last_name: data.nom.trim(),
        email: data.email.trim(),
        company: data.entreprise.trim(),
        phone: `+33${data.telephone.replace(/\s+/g, "")}`,
        message: [`Vous représentez : ${representeLabel}`, `Entreprise : ${data.entreprise.trim()}`].join(
          "\n"
        ),
        origin: "organisme-formation",
      });

      setSubmitted(true);
    } catch {
      toast.error("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SuccessState />;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {/* Nom / Prénom */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          id="nom"
          label="Votre nom *"
          placeholder="Nom"
          autoComplete="family-name"
          value={data.nom}
          error={errors.nom}
          onChange={handleChange}
        />
        <TextField
          id="prenom"
          label="Prénom *"
          placeholder="Prénom"
          autoComplete="given-name"
          value={data.prenom}
          error={errors.prenom}
          onChange={handleChange}
        />
      </div>

      {/* Email professionnel */}
      <TextField
        id="email"
        type="email"
        label="Email professionnel *"
        placeholder="Email"
        autoComplete="email"
        value={data.email}
        error={errors.email}
        onChange={handleChange}
      />

      {/* Un numéro pour vous joindre */}
      <div>
        <label htmlFor="telephone" className="mb-2 block text-base text-[#1B365E]">
          Un numéro pour vous joindre *
        </label>
        <div className="flex gap-2.5">
          <span
            className="flex h-10 w-14 shrink-0 items-center justify-center rounded-lg border border-[#EFEFEF] bg-white text-[12px] text-[#ABABAB]"
            aria-hidden="true"
          >
            +33
          </span>
          <input
            id="telephone"
            name="telephone"
            type="tel"
            inputMode="tel"
            value={data.telephone}
            onChange={handleChange}
            placeholder="6 12 34 56 78"
            autoComplete="tel-national"
            aria-required="true"
            aria-invalid={!!errors.telephone}
            aria-describedby={errors.telephone ? "telephone-error" : undefined}
            className={errors.telephone ? INPUT_ERROR_CLASS : INPUT_CLASS}
          />
        </div>
        {errors.telephone && (
          <p id="telephone-error" role="alert" aria-live="polite" className={ERROR_CLASS}>
            {errors.telephone}
          </p>
        )}
      </div>
      {/* Vous représentez */}
      <div>
        <FloatingSelect
          label="Vous représentez *"
          name="represente"
          value={data.represente}
          onChange={handleChange}
          options={REPRESENTE_OPTIONS}
          placeholder="Veuillez sélectionner"
          lessGap
        />
        {errors.represente && (
          <p id="represente-error" role="alert" aria-live="polite" className={ERROR_CLASS}>
            {errors.represente}
          </p>
        )}
      </div>

      {/* Entreprise */}
      <TextField
        id="entreprise"
        label="Entreprise *"
        placeholder="Nom de votre entreprise"
        autoComplete="organization"
        value={data.entreprise}
        error={errors.entreprise}
        onChange={handleChange}
      />


      <Button
        type="submit"
        variant="gradient"
        size="unset"
        disabled={submitting}
        className="mt-1 h-13 w-full rounded-lg text-[16px]"
      >
        {submitting ? (
          "Envoi en cours…"
        ) : (
          <span className="flex items-center gap-2">
            Référencer mon organisme
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </span>
        )}
      </Button>

      <p className="flex items-center justify-center gap-1.5 text-[12px] text-[#1D1D1D]/70">
        <ShieldCheck className="h-4 w-4 text-[#40BFDE]" aria-hidden="true" />
        Gratuit et sans engagement — réponse sous 24&nbsp;h
      </p>
    </form>
  );
};

export default RegistrationForm;
